/**
 * Cómo se lee una fecha o un horario, en correos y en pantallas por igual.
 * Español de Perú. Sin dependencias: lo usan la API y la web.
 */
import type { Fecha, Hora } from './tipos.js';

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'setiembre', 'octubre', 'noviembre', 'diciembre'];

function partes(fecha: Fecha): { anio: number; mes: number; dia: number } {
  const [anio, mes, dia] = fecha.split('-').map(Number);
  return { anio: anio!, mes: mes!, dia: dia! };
}

/** 0 = domingo. */
export function diaSemana(fecha: Fecha): number {
  const { anio, mes, dia } = partes(fecha);
  return new Date(Date.UTC(anio, mes - 1, dia)).getUTCDay();
}

export const nombreDia = (fecha: Fecha) => DIAS[diaSemana(fecha)]!;
export const nombreMes = (mes: number) => MESES[mes - 1]!;

/** "viernes 25 de setiembre" */
export function fechaLarga(fecha: Fecha): string {
  const { mes, dia } = partes(fecha);
  return `${nombreDia(fecha)} ${dia} de ${nombreMes(mes)}`;
}

/** "10:00" */
export const hhmm = (h: Hora) => `${String(h).padStart(2, '0')}:00`;

/**
 * Las horas agrupadas en tramos seguidos: [10, 11, 14] se lee
 * "10:00 a 12:00 y 14:00 a 15:00", que es como lo piensa una persona.
 */
export function tramos(horas: Hora[]): string {
  const orden = [...horas].sort((a, b) => a - b);
  const partesTexto: string[] = [];
  let inicio = orden[0];
  for (let i = 0; i < orden.length; i++) {
    const h = orden[i]!;
    if (orden[i + 1] !== h + 1) {
      partesTexto.push(`${hhmm(inicio!)} a ${hhmm(h + 1)}`);
      inicio = orden[i + 1];
    }
  }
  if (partesTexto.length <= 1) return partesTexto[0] ?? '';
  return `${partesTexto.slice(0, -1).join(', ')} y ${partesTexto.at(-1)}`;
}

/** 45000 céntimos → "S/450"; 15050 → "S/150.50". */
export function soles(centimos: number): string {
  const s = centimos / 100;
  return `S/${Number.isInteger(s) ? s : s.toFixed(2)}`;
}
